import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useParticipante } from '../../../contexts/ParticipanteContext';
import { StreakCounter } from '../../../components/gamification/StreakCounter';
import { DailyChallenges } from '../../../components/gamification/DailyChallenges';
import { FriendsRanking } from '../../../components/gamification/FriendsRanking';
import { FirstTimeBadges } from '../../../components/gamification/FirstTimeBadges';
import { ChatbotAvatar } from '../../../components/chatbot/ChatbotAvatar';
import CollapsibleSection from './components/CollapsibleSection';
import ComunicadoModal, { STORAGE_KEY } from './components/ComunicadoModal';

const ParticipanteHome: React.FC = () => {
  const { participante, campanhaAtual } = useParticipante();

  // Dados reativos à campanha selecionada
  const pontosDisponiveis = campanhaAtual?.progressao?.pontosDisponiveis ?? participante?.pontosDisponiveis ?? 0;
  const xpAtual = campanhaAtual?.progressao?.xpAtual ?? participante?.xpAtual ?? 0;
  const missoesCompletas = campanhaAtual?.progressao?.missoesCompletas ?? participante?.missoesCompletas ?? 0;
  const rankingAtual = campanhaAtual?.progressao?.ranking ?? 0;
  const totalParticipantes = campanhaAtual?.progressao?.totalParticipantes ?? 0;
  const navigate = useNavigate();
  const [showConquistaModal, setShowConquistaModal] = useState(false);
  const [conquistaSelecionada, setConquistaSelecionada] = useState<any>(null);
  const [showBanner, setShowBanner] = useState(true);
  const [showComunicado, setShowComunicado] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowBanner(false), 10000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      setShowComunicado(true);
    }
  }, []);

  const acoesRapidas = [
    { icon: 'ri-trophy-line', label: 'Campanhas', path: '/premiado/campanhas', cor: 'from-orange-500 to-orange-600' },
    { icon: 'ri-flag-line', label: 'Missões', path: '/premiado/missoes', cor: 'from-teal-500 to-teal-600' },
    { icon: 'ri-gift-line', label: 'Catálogo', path: '/premiado/recompensas', cor: 'from-emerald-500 to-emerald-600', state: { abaInicial: 'produtos' } },
    { icon: 'ri-gamepad-line', label: 'Jogos', path: '/premiado/jogos', cor: 'from-amber-500 to-amber-600' }
  ];

  const campanhasAtivas = [
    { id: 1, nome: 'Campanha Black Friday 2024', progresso: 75, pontosGanhos: 3500, metaTotal: 5000, diasRestantes: 12, cor: 'orange' },
    { id: 2, nome: 'Desafio Trimestral Q4', progresso: 45, pontosGanhos: 2250, metaTotal: 5000, diasRestantes: 45, cor: 'teal' },
    { id: 3, nome: 'Programa Fidelidade Premium', progresso: 90, pontosGanhos: 4500, metaTotal: 5000, diasRestantes: 8, cor: 'emerald' }
  ];

  const missoesDestaque = [
    { id: 1, nome: 'Venda 10 produtos premium', xp: 500, progresso: 70, atual: 7, meta: 10, expiraEm: '2 dias', tipo: 'Vendas' },
    { id: 2, nome: 'Ative 5 novos clientes', xp: 300, progresso: 40, atual: 2, meta: 5, expiraEm: '5 dias', tipo: 'Clientes' },
    { id: 3, nome: 'Complete 20 atendimentos', xp: 200, progresso: 85, atual: 17, meta: 20, expiraEm: '1 dia', tipo: 'Suporte' }
  ];

  const atividadesRecentes = [
    { tipo: 'xp', descricao: 'Ganhou 500 XP na missão "Venda Premium"', tempo: '2 horas atrás', icone: 'ri-star-line', cor: 'text-yellow-600' },
    { tipo: 'pontos', descricao: 'Recebeu 1.500 pontos da Campanha Black Friday', tempo: '5 horas atrás', icone: 'ri-coin-line', cor: 'text-orange-600' },
    { tipo: 'distintivo', descricao: 'Conquistou o distintivo "Vendedor Estrela"', tempo: '1 dia atrás', icone: 'ri-medal-line', cor: 'text-amber-600' },
    { tipo: 'resgate', descricao: 'Resgatou Vale-Presente Amazon R$ 100', tempo: '2 dias atrás', icone: 'ri-gift-line', cor: 'text-emerald-600' },
    { tipo: 'nivel', descricao: 'Subiu para o nível Ouro!', tempo: '3 dias atrás', icone: 'ri-trophy-line', cor: 'text-yellow-500' }
  ];

  const notificacoes = [
    { tipo: 'alerta', mensagem: 'Missão "Venda Premium" expira em 2 dias!', cor: 'bg-red-50 text-red-700 border-red-200' },
    { tipo: 'info', mensagem: 'Nova campanha disponível: "Natal 2024"', cor: 'bg-teal-50 text-teal-700 border-teal-200' },
    { tipo: 'sucesso', mensagem: 'Você está no Top 10 do ranking geral!', cor: 'bg-green-50 text-green-700 border-green-200' }
  ];

  const conquistasRecentes = [
    { id: 1, tipo: 'distintivo', titulo: 'Vendedor Estrela', descricao: 'Conquistou o distintivo "Vendedor Estrela"', icone: '⭐', tempo: '1 dia atrás', cor: 'from-yellow-400 to-yellow-600' },
    { id: 2, tipo: 'nivel', titulo: 'Nível Ouro', descricao: 'Subiu para o nível Ouro!', icone: '🏆', tempo: '3 dias atrás', cor: 'from-yellow-500 to-orange-500' },
    { id: 3, tipo: 'missao', titulo: 'Missão Completa', descricao: 'Completou a missão "Venda Premium"', icone: '🎯', tempo: '5 dias atrás', cor: 'from-teal-500 to-teal-600' }
  ];

  const dadosGrafico = [
    { mes: 'Jan', pontos: 2500, xp: 1200 },
    { mes: 'Fev', pontos: 3200, xp: 1800 },
    { mes: 'Mar', pontos: 2800, xp: 1500 },
    { mes: 'Abr', pontos: 4100, xp: 2300 },
    { mes: 'Mai', pontos: 3800, xp: 2100 },
    { mes: 'Jun', pontos: 5200, xp: 2900 }
  ];

  const maxPontos = Math.max(...dadosGrafico.map(d => d.pontos));
  const maxXP = Math.max(...dadosGrafico.map(d => d.xp));

  const features = [
    { icon: 'ri-trophy-line', title: 'Pioneirismo', description: 'Primeira plataforma 100% aderente', color: 'from-yellow-400 to-orange-500' },
    { icon: 'ri-lightbulb-flash-line', title: 'Inovação', description: 'Inovação com conformidade', color: 'from-teal-400 to-cyan-500' },
    { icon: 'ri-shield-check-line', title: 'Compliance', description: 'Compliance em primeiro lugar', color: 'from-green-400 to-emerald-500' },
    { icon: 'ri-lock-2-line', title: 'Segurança', description: 'Segurança e conformidade', color: 'from-slate-400 to-slate-600' }
  ];

  return (
    <div className="space-y-5 pb-20 lg:pb-6">
      {/* Header com Saudação */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-1">Olá, {participante?.nome?.split(' ')[0]}! 👋</h1>
            <p className="text-orange-100">Bem-vindo de volta à sua jornada de conquistas</p>
          </div>
          <div className="hidden sm:block">
            <img
              src={participante?.foto}
              alt={participante?.nome}
              className="w-20 h-20 rounded-full border-4 border-white/30 object-cover"
            />
          </div>
        </div>
      </div>

      {/* Banner de Conformidade */}
      {showBanner && (
        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 rounded-3xl">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="absolute top-4 right-4 text-white/20 text-4xl animate-bounce">
            <i className="ri-shield-star-line"></i>
          </div>
          <div className="relative p-8 lg:p-10">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <div className="relative group cursor-pointer">
                  <div className="absolute inset-0 bg-white/30 rounded-3xl blur-xl group-hover:blur-2xl transition-all"></div>
                  <div className="relative w-32 h-32 bg-white rounded-3xl flex items-center justify-center transform group-hover:scale-110 transition-all duration-300">
                    <i className="ri-shield-star-fill text-6xl text-emerald-500"></i>
                  </div>
                </div>
              </div>
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4">
                  <i className="ri-award-fill text-yellow-300 text-lg"></i>
                  <span className="text-white font-bold text-sm tracking-wide">PIONEIRISMO E INOVAÇÃO</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-black text-white mb-2 leading-tight">
                  A primeira plataforma de incentivos{' '}
                  <span className="text-yellow-300">100% aderente</span>{' '}
                  às regras e legislações vigentes.
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
                  {features.map((feature, index) => (
                    <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer group">
                      <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                        <i className={`${feature.icon} text-2xl text-white`}></i>
                      </div>
                      <h4 className="text-white font-bold text-sm mb-1">{feature.title}</h4>
                      <p className="text-white/80 text-xs leading-tight">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cards de Saldo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-slate-200 hover:border-orange-200 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <i className="ri-coin-line text-xl text-orange-600"></i>
            </div>
            <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">+12%</span>
          </div>
          <p className="text-xs text-slate-500 mb-1">Pontos Disponíveis</p>
          <p className="text-xl font-bold text-slate-900">{pontosDisponiveis.toLocaleString('pt-BR')}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 hover:border-yellow-200 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-star-line text-xl text-yellow-600"></i>
            </div>
            <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">+8%</span>
          </div>
          <p className="text-xs text-slate-500 mb-1">XP Acumulado</p>
          <p className="text-xl font-bold text-slate-900">{xpAtual.toLocaleString('pt-BR')}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 hover:border-amber-200 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <i className="ri-medal-line text-xl text-amber-600"></i>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-1">Missões Completas</p>
          <p className="text-xl font-bold text-slate-900">{missoesCompletas}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 hover:border-teal-200 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <i className="ri-trophy-line text-xl text-teal-600"></i>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-1">{rankingAtual > 0 ? 'Ranking' : 'Campanhas Ativas'}</p>
          <p className="text-xl font-bold text-slate-900">
            {rankingAtual > 0 ? `#${rankingAtual}${totalParticipantes > 0 ? ` / ${totalParticipantes}` : ''}` : participante?.campanhasAtivas}
          </p>
        </div>
      </div>

      {/* Notificações */}
      {notificacoes.length > 0 && (
        <div className="space-y-2">
          {notificacoes.map((notif, index) => (
            <div key={index} className={`${notif.cor} border rounded-lg px-4 py-3 flex items-center gap-3`}>
              <i className="ri-information-line text-lg flex-shrink-0"></i>
              <p className="text-sm font-medium flex-1">{notif.mensagem}</p>
              <button className="text-xs font-semibold hover:underline whitespace-nowrap cursor-pointer">Ver mais</button>
            </div>
          ))}
        </div>
      )}

      {/* Perfil + Streak lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Card de Progresso do Perfil */}
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-5 border border-teal-200">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-11 h-11 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-user-star-line text-xl text-white"></i>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-slate-900">Complete seu Perfil</h3>
                <p className="text-xs text-slate-600">Desbloqueie benefícios exclusivos</p>
              </div>
            </div>
            <div className="relative w-14 h-14 flex-shrink-0">
              <svg className="w-14 h-14 transform -rotate-90">
                <circle cx="28" cy="28" r="24" stroke="currentColor" strokeWidth="5" fill="none" className="text-teal-100" />
                <circle cx="28" cy="28" r="24" stroke="currentColor" strokeWidth="5" fill="none"
                  strokeDasharray={`${2 * Math.PI * 24}`}
                  strokeDashoffset={`${2 * Math.PI * 24 * 0.25}`}
                  className="text-teal-500 transition-all duration-500" strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold text-teal-600">75%</span>
              </div>
            </div>
            <Link to="/premiado/perfil" className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg text-xs font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all whitespace-nowrap flex-shrink-0 cursor-pointer">
              Completar
            </Link>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-full border border-teal-200">
              <i className="ri-map-pin-line text-orange-500 text-xs"></i>
              <span className="text-xs font-medium text-slate-700">Endereço</span>
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-full border border-teal-200">
              <i className="ri-bank-card-line text-orange-500 text-xs"></i>
              <span className="text-xs font-medium text-slate-700">Dados bancários</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-teal-700 font-medium ml-auto">
              <i className="ri-gift-line"></i>
              <span>+200 XP ao completar</span>
            </div>
          </div>
        </div>

        {/* Streak */}
        <StreakCounter />
      </div>

      {/* Ações Rápidas */}
      <div>
        <h2 className="text-base font-bold text-slate-900 mb-3">Ações Rápidas</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {acoesRapidas.map((acao, index) => (
            <Link key={index} to={acao.path} state={acao.state}
              className="bg-white rounded-xl p-5 border border-slate-200 hover:border-orange-200 hover:-translate-y-1 transition-all group cursor-pointer">
              <div className={`w-12 h-12 bg-gradient-to-br ${acao.cor} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                <i className={`${acao.icon} text-xl text-white`}></i>
              </div>
              <p className="text-sm font-semibold text-slate-900">{acao.label}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Seção: Desafios Diários (colapsável) */}
      <CollapsibleSection title="Desafios Diários" icon="ri-fire-line" defaultOpen={true} badge="3" badgeColor="bg-orange-100 text-orange-700" accent="border-orange-200">
        <DailyChallenges />
      </CollapsibleSection>

      {/* Seção: Campanhas Ativas (colapsável) */}
      <CollapsibleSection title="Minhas Campanhas" icon="ri-trophy-line" defaultOpen={true} badge={campanhasAtivas.length} badgeColor="bg-teal-100 text-teal-700" accent="border-teal-200">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-2">
          {campanhasAtivas.map((campanha) => (
            <Link key={campanha.id} to={`/premiado/campanhas/${campanha.id}`}
              className="bg-slate-50 rounded-xl p-5 border border-slate-200 hover:border-orange-300 hover:-translate-y-1 transition-all cursor-pointer">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-bold text-slate-900 flex-1">{campanha.nome}</h3>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold text-white bg-${campanha.cor}-500 whitespace-nowrap ml-2`}>
                  {campanha.diasRestantes}d
                </span>
              </div>
              <div className="space-y-2">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-500">Progresso</span>
                    <span className="text-xs font-bold text-slate-900">{campanha.progresso}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className={`h-full bg-${campanha.cor}-500 rounded-full`} style={{ width: `${campanha.progresso}%` }}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Pontos ganhos</span>
                  <span className="font-bold text-slate-900">{campanha.pontosGanhos.toLocaleString('pt-BR')}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-3 text-right">
          <Link to="/premiado/campanhas" className="text-sm font-semibold text-orange-600 hover:text-orange-700 cursor-pointer">
            Ver todas <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
      </CollapsibleSection>

      {/* Seção: Missões Expirando (colapsável) */}
      <CollapsibleSection title="Missões Expirando em Breve" icon="ri-alarm-warning-line" defaultOpen={true} badge="2" badgeColor="bg-red-100 text-red-700" accent="border-red-200">
        <div className="space-y-3 mt-2">
          {missoesDestaque.slice(0, 2).map((missao) => (
            <div key={missao.id} className="bg-red-50 rounded-lg p-4 border border-red-200">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">{missao.tipo}</span>
                    <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      Expira em {missao.expiraEm}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-slate-900">{missao.nome}</h3>
                </div>
                <div className="flex items-center gap-1 text-yellow-600 ml-3">
                  <i className="ri-star-fill text-base"></i>
                  <span className="text-sm font-bold">{missao.xp}</span>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Progresso</span>
                  <span className="font-bold text-slate-900">{missao.atual}/{missao.meta}</span>
                </div>
                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full" style={{ width: `${missao.progresso}%` }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-right">
          <Link to="/premiado/missoes" className="text-sm font-semibold text-red-600 hover:text-red-700 cursor-pointer">
            Ver todas <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
      </CollapsibleSection>

      {/* Seção: Ranking de Amigos (colapsável, fechado por padrão) */}
      <CollapsibleSection title="Ranking de Amigos" icon="ri-group-line" defaultOpen={false} accent="border-slate-200">
        <FriendsRanking />
      </CollapsibleSection>

      {/* Seção: Conquistas Recentes (colapsável) */}
      <CollapsibleSection title="Conquistas Recentes" icon="ri-medal-line" defaultOpen={true} badge={conquistasRecentes.length} badgeColor="bg-amber-100 text-amber-700" accent="border-amber-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          {conquistasRecentes.map((conquista, index) => (
            <button key={conquista.id} onClick={() => { setConquistaSelecionada(conquista); setShowConquistaModal(true); }}
              className="bg-slate-50 rounded-xl p-5 border border-slate-200 hover:border-amber-300 hover:-translate-y-1 transition-all text-left group cursor-pointer"
              style={{ animation: `slideInUp 0.5s ease-out ${index * 0.1}s both` }}>
              <div className={`w-14 h-14 bg-gradient-to-br ${conquista.cor} rounded-xl flex items-center justify-center mb-3 text-2xl group-hover:scale-110 transition-transform`}>
                {conquista.icone}
              </div>
              <h3 className="text-sm font-bold text-slate-900 mb-1">{conquista.titulo}</h3>
              <p className="text-xs text-slate-500">{conquista.tempo}</p>
            </button>
          ))}
        </div>
      </CollapsibleSection>

      {/* Seção: Badges de Primeira Vez (colapsável, fechado por padrão) */}
      <CollapsibleSection title="Distintivos Especiais" icon="ri-award-line" defaultOpen={false} accent="border-slate-200">
        <FirstTimeBadges />
      </CollapsibleSection>

      {/* Seção: Gráfico de Evolução (colapsável, fechado por padrão) */}
      <CollapsibleSection title="Evolução de Pontos e XP" icon="ri-bar-chart-2-line" defaultOpen={false} accent="border-slate-200">
        <div className="relative h-56 mt-2">
          <div className="absolute inset-0 flex flex-col justify-between">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="border-t border-slate-100"></div>
            ))}
          </div>
          <div className="absolute inset-0 flex items-end justify-around px-4">
            {dadosGrafico.map((dado, index) => (
              <div key={index} className="flex flex-col items-center gap-2 flex-1 max-w-[80px]">
                <div className="w-full flex gap-1 items-end h-44">
                  <div className="relative flex-1 group">
                    <div className="w-full bg-gradient-to-t from-orange-500 to-orange-400 rounded-t-lg transition-all hover:from-orange-600 hover:to-orange-500 cursor-pointer"
                      style={{ height: `${(dado.pontos / maxPontos) * 100}%` }}>
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-2 py-1 rounded text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {dado.pontos.toLocaleString('pt-BR')} pts
                      </div>
                    </div>
                  </div>
                  <div className="relative flex-1 group">
                    <div className="w-full bg-gradient-to-t from-yellow-500 to-yellow-400 rounded-t-lg transition-all hover:from-yellow-600 hover:to-yellow-500 cursor-pointer"
                      style={{ height: `${(dado.xp / maxXP) * 100}%` }}>
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-2 py-1 rounded text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {dado.xp.toLocaleString('pt-BR')} XP
                      </div>
                    </div>
                  </div>
                </div>
                <span className="text-xs font-semibold text-slate-600">{dado.mes}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs mt-3">
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-orange-500"></div><span className="text-slate-600">Pontos</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-yellow-500"></div><span className="text-slate-600">XP</span></div>
        </div>
      </CollapsibleSection>

      {/* Seção: Atividades Recentes (colapsável, fechado por padrão) */}
      <CollapsibleSection title="Atividades Recentes" icon="ri-history-line" defaultOpen={false} accent="border-slate-200">
        <div className="divide-y divide-slate-100 mt-1">
          {atividadesRecentes.map((atividade, index) => (
            <div key={index} className="py-3 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                <i className={`${atividade.icone} text-lg ${atividade.cor}`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">{atividade.descricao}</p>
                <p className="text-xs text-slate-500 mt-0.5">{atividade.tempo}</p>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Modal de Conquista */}
      {showConquistaModal && conquistaSelecionada && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden animate-[scaleIn_0.3s_ease-out]">
            <div className={`bg-gradient-to-br ${conquistaSelecionada.cor} p-8 text-center`}>
              <div className="text-7xl mb-4 animate-[bounce_1s_ease-in-out_infinite]">{conquistaSelecionada.icone}</div>
              <h2 className="text-2xl font-bold text-white mb-2">Parabéns!</h2>
              <p className="text-white/90">{conquistaSelecionada.titulo}</p>
            </div>
            <div className="p-6">
              <p className="text-slate-700 text-center mb-6">{conquistaSelecionada.descricao}</p>
              <button onClick={() => setShowConquistaModal(false)}
                className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer">
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      <ChatbotAvatar />

      {showComunicado && (
        <ComunicadoModal onClose={() => setShowComunicado(false)} />
      )}

      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default ParticipanteHome;
